import React, { useState } from 'react';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { X, Palette, ChevronDown, Check, Plus } from 'lucide-react';
import { setBrandKit } from '../../features/brand-kit/brandKitSlice';
import { useGetBrandKitsQuery } from '../../features/brand-kit/brandKitApi';

interface BrandKitPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyColor: (color: string, mode?: 'text' | 'background') => void;
  onApplyFont: (fontFamily: string) => void;
  onAddLogo: (logoUrl: string) => void;
}

export function BrandKitPanel({ isOpen, onClose, onApplyColor, onApplyFont, onAddLogo }: BrandKitPanelProps) {
  // Read real data from Redux
  const dispatch = useAppDispatch();
  const brandKit = useAppSelector(state => state.brandKit);
  const { data: kitsResponse } = useGetBrandKitsQuery();
  const availableKits = kitsResponse?.data || [];

  // Auto-select first kit if none selected
  React.useEffect(() => {
    const currentId = brandKit.id || (brandKit as any)._id;
    if (availableKits.length > 0 && !currentId) {
      dispatch(setBrandKit(availableKits[0]));
    }
  }, [availableKits, brandKit, dispatch]);

  const [activeTab, setActiveTab] = useState<'logos' | 'colors' | 'typography'>('logos');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleKitSelect = (kit: any) => {
    dispatch(setBrandKit(kit));
    setIsDropdownOpen(false);
  };

  // Map Typography to array for rendering
  const typography = brandKit.typography ? [
    { label: 'Title', ...(brandKit.typography.title || {}) },
    { label: 'Subtitle', ...(brandKit.typography.subtitle || {}) },
    { label: 'Heading', ...(brandKit.typography.heading || {}) },
    { label: 'Subheading', ...(brandKit.typography.subheading || {}) },
    { label: 'Body', ...(brandKit.typography.body || {}) },
    { label: 'Small Heading', ...(brandKit.typography.smallHeading || {}) },
    { label: 'Quote', ...(brandKit.typography.quote || {}) },
    { label: 'Caption', ...(brandKit.typography.caption || {}) },
  ] : [];

  // Map Logos to array
  const logos = [
    brandKit.logos.main ? { id: 'main', url: brandKit.logos.main, name: 'Main Logo' } : null,
    brandKit.logos.secondary ? { id: 'secondary', url: brandKit.logos.secondary, name: 'Secondary Logo' } : null,
    brandKit.logos.light ? { id: 'light', url: brandKit.logos.light, name: 'Light Logo' } : null,
    brandKit.logos.dark ? { id: 'dark', url: brandKit.logos.dark, name: 'Dark Logo' } : null,
  ].filter((l): l is { id: string; url: string; name: string } => !!l);

  // Check if we have any assets
  const hasAssets = brandKit.name !== '' && (brandKit.colors.length > 0 || logos.length > 0 || (brandKit.typography && Object.keys(brandKit.typography).length > 0));

  return (
    <>
      {/* Backdrop */}


      {/* Panel */}
      <div
        className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between border-b border-gray-100 bg-white z-10">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Brand Kit</h2>
            <p className="text-xs text-gray-500 mt-0.5">Manage your brand assets</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Brand Kit Selector - Fixed above tabs */}
        {hasAssets && (
          <div className="px-6 pb-2 pt-2 relative z-20">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-900 text-sm rounded-lg p-2.5 font-medium flex items-center justify-between transition-colors"
            >
              <span>{brandKit.name}</span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                />
                <div className="absolute left-6 right-6 top-[calc(100%+4px)] bg-white rounded-lg shadow-xl border border-gray-100 z-20 overflow-hidden">
                  <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                    {availableKits.map((kit: any) => {
                      const kitId = kit.id || kit._id;
                      const activeId = brandKit.id || (brandKit as any)._id;
                      const isActive = activeId === kitId;

                      return (
                        <button
                          key={kitId}
                          type="button"
                          onClick={() => handleKitSelect(kit)}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between group transition-colors"
                        >
                          <span className={`font-medium ${isActive ? 'text-[#0BA29B]' : 'text-gray-700'}`}>
                            {kit.name}
                          </span>
                          {isActive && (
                            <Check className="w-4 h-4 text-[#0BA29B]" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="border-t border-gray-100 p-2 bg-gray-50/50">
                    <button type="button" className="w-full flex items-center justify-center gap-2 text-xs text-[#0BA29B] font-medium py-2 hover:bg-[#0BA29B]/10 rounded transition-colors">
                      <Plus className="w-3 h-3" />
                      Create New Kit
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        {hasAssets && (
          <div className="flex p-2 bg-gray-50/50 border-b border-gray-100 shrink-0 gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('logos')}
              className="flex-1 py-2 text-sm font-medium transition-all relative rounded-lg overflow-hidden group cursor-pointer"
            >
              {/* Active State Indicator */}
              <div 
                  className="app-selector-indicator" 
                  style={{ '--background-width': activeTab === 'logos' ? '100%' : '0%' } as React.CSSProperties} 
              />
              <span className={clsx("relative z-10 transition-colors duration-400", activeTab === 'logos' ? "app-active-state" : "text-gray-500 hover:text-gray-700")}>
                Logo
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('colors')}
              className="flex-1 py-2 text-sm font-medium transition-all relative rounded-lg overflow-hidden group cursor-pointer"
            >
              {/* Active State Indicator */}
              <div 
                  className="app-selector-indicator" 
                  style={{ '--background-width': activeTab === 'colors' ? '100%' : '0%' } as React.CSSProperties} 
              />
              <span className={clsx("relative z-10 transition-colors duration-400", activeTab === 'colors' ? "app-active-state" : "text-gray-500 hover:text-gray-700")}>
                Colors
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('typography')}
              className="flex-1 py-2 text-sm font-medium transition-all relative rounded-lg overflow-hidden group cursor-pointer"
            >
              {/* Active State Indicator */}
              <div 
                  className="app-selector-indicator" 
                  style={{ '--background-width': activeTab === 'typography' ? '100%' : '0%' } as React.CSSProperties} 
              />
              <span className={clsx("relative z-10 transition-colors duration-400", activeTab === 'typography' ? "app-active-state" : "text-gray-500 hover:text-gray-700")}>
                Typography
              </span>
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar">
          {!hasAssets ? (
            <div className="flex flex-col items-center justify-center py-12 text-center h-full">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <Palette className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2">No brand assets available</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-[200px]">Configure your Brand Kit in settings to see your assets here.</p>
            </div>
          ) : (
            <div className="py-6 space-y-6">

              {/* Tab Content */}

              {/* Brand Logos */}
              {activeTab === 'logos' && (
                <div>
                  {logos.length === 0 ? (
                    <div className="text-xs text-gray-400 italic text-center py-8">No logos uploaded.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {logos.map((logo) => (
                        <button
                          key={logo.id}
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('element-type', 'brand-logo');
                            e.dataTransfer.setData('logo-url', logo.url);
                          }}
                          onClick={() => onAddLogo(logo.url)}
                          className="aspect-square rounded-lg border border-gray-200 p-2 flex flex-col items-center justify-center bg-gray-50 hover:bg-white hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 transition-all active:scale-95 group relative"
                        >
                          <img src={logo.url} alt={logo.name} className="max-w-full max-h-[80%] object-contain mb-1" />
                          <span className="text-[10px] text-gray-400">{logo.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Brand Colors */}
              {activeTab === 'colors' && (
                <div>
                  <div className="space-y-4">
                    {brandKit.colors.length === 0 && (
                      <div className="text-xs text-gray-400 italic text-center py-8">No color palettes added.</div>
                    )}
                    {brandKit.colors.map((palette: any) => (
                      <div key={palette.id}>
                        <p className="text-xs font-medium text-gray-500 mb-2">{palette.name}</p>
                        <div className="grid grid-cols-5 gap-2">
                          {palette.colors.map((color: string, i: number) => (
                            <div key={i} className="group relative">
                              <button
                                type="button"
                                onClick={() => onApplyColor(color, 'text')}
                                className="w-full aspect-square rounded-lg border border-gray-200 shadow-sm transition-all active:scale-95 hover:ring-2 hover:ring-[#30A84B]/20 hover:border-[#30A84B]/30 overflow-hidden relative group/btn"
                                title={`Apply ${color} to text`}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('element-type', 'brand-color');
                                  e.dataTransfer.setData('color-value', color);
                                }}
                              >
                                <div className="w-full h-full" style={{ backgroundColor: color }} />

                                {/* Quick Actions Overlay */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/btn:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                  <div className="text-[8px] font-black text-white uppercase tracking-tighter bg-[#30A84B] px-1 rounded-sm">Text</div>
                                </div>
                              </button>

                              {/* Background Apply Dot */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onApplyColor(color, 'background');
                                }}
                                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-gray-100 shadow-sm hover:scale-110 transition-transform flex items-center justify-center group/bg"
                                title="Apply as text background"
                              >
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[8px] font-bold py-1 px-2 rounded opacity-0 group-hover/bg:opacity-100 pointer-events-none whitespace-nowrap z-50">
                                  Apply to Background
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Brand Fonts */}
              {activeTab === 'typography' && (
                <div>
                  <div className="space-y-2">
                    {typography.map((font: any, _i: number) => (
                      <button
                        key={font.label}
                        type="button"
                        onClick={() => onApplyFont(font.fontFamily)}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#30A84B]/30 hover:bg-gray-50 hover:ring-2 hover:ring-[#30A84B]/20 transition-all group"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">{font.label}</span>
                        </div>
                        <div
                          className="text-gray-900 text-lg group-hover:text-black truncate"
                          style={{
                            fontFamily: font.fontFamily,
                            fontWeight: font.isBold ? 'bold' : 'normal',
                            fontStyle: font.isItalic ? 'italic' : 'normal',
                            textTransform: font.isUppercase ? 'uppercase' : 'none'
                          }}
                        >
                          {font.fontFamily}
                        </div>
                        <div className="text-xs text-gray-400 mt-1 flex gap-2">
                          <span>{font.fontSize}px</span>
                          {font.isBold && <span>• Bold</span>}
                          {font.isItalic && <span>• Italic</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );
}

